import React, { useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email?.trim()) {
      newErrors.email = '이메일을 입력해주세요';
    } else if (!/^([^\s@]+)@([^\s@]+)\.([^\s@]+)$/?.test(formData?.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
    }
    
    if (!formData?.password) {
      newErrors.password = '비밀번호를 입력해주세요';
    } else if (formData?.password?.length < 6) {
      newErrors.password = '비밀번호는 최소 6자 이상이어야 합니다';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (isLocked) {
      setErrors({ general: '너무 많은 로그인 시도로 인해 계정이 일시적으로 잠겼습니다. 5분 후 다시 시도해주세요.' });
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData?.email,
        password: formData?.password,
      });

      if (error) {
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);

        if (newAttempts >= 5) {
          setIsLocked(true);
          setErrors({
            general: '너무 많은 로그인 시도로 인해 계정이 일시적으로 잠겼습니다. 5분 후 다시 시도해주세요.'
          });
          setTimeout(() => {
            setIsLocked(false);
            setLoginAttempts(0);
          }, 300000);
        } else {
          setErrors({
            general: `이메일 또는 비밀번호가 올바르지 않습니다. (${newAttempts}/5 시도)`
          });
        }
        return;
      }

      setErrors({});
      setLoginAttempts(0);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors?.general && (
        <div className="flex items-start space-x-3 rounded-lg bg-error/10 p-4">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
          <p className="text-sm text-error">{errors?.general}</p>
        </div>
      )}
      <Input
        label="이메일"
        type="email"
        name="email"
        placeholder="admin@example.com"
        value={formData?.email}
        onChange={handleInputChange}
        error={errors?.email}
        required
        disabled={isLocked}
      />
      <div className="relative">
        <Input
          label="비밀번호"
          type={showPassword ? 'text' : 'password'}
          name="password"
          placeholder="비밀번호를 입력하세요"
          value={formData?.password}
          onChange={handleInputChange}
          error={errors?.password}
          required
          disabled={isLocked}
        />
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors duration-150"
          aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>
      <div className="flex items-center justify-end">
        <button
          type="button"
          className="text-sm text-accent hover:text-accent/80 transition-colors duration-150"
          onClick={() => alert('비밀번호 복구 기능은 개발 중입니다.')}
        >
          비밀번호를 잊으셨나요?
        </button>
      </div>
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        disabled={isLocked}
        iconName="LogIn"
        iconPosition="right"
      >
        대시보드 로그인
      </Button>
    </form>
  );
};

export default LoginForm;